import React, { useCallback, useEffect, useRef, useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import MemoryIcon from '@mui/icons-material/Memory';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';

import withShipment from '../../withShipment';
import CustomToolbar from '../global/CustomToolbar';
import { Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { userInfoSelector, viewOnlySelector } from '../../redux/selectors/auth';
import { raffleApi } from './raffleApi';
import DrawFlow from './DrawFlow';
import HistoryView from './HistoryView';
import NumbersView from './NumbersView';
import SettingsView from './SettingsView';
import { Notice, RaffleErrorBoundary, Spinner } from './RafflePieces';
import { INDIGO, SLATE } from './raffleTokens';
import './Raffle.css';

const TITLES = {
  history: 'Raffle History',
  settings: 'Raffle Settings',
  numbers: 'Customer Raffle Numbers',
  draw: 'Raffle',
};

const MODES = [
  {
    key: 'machine',
    label: 'Machine Number',
    short: 'Machine',
    Icon: MemoryIcon,
  },
  {
    key: 'ticket',
    label: 'Ticket Number',
    short: 'Ticket',
    Icon: ConfirmationNumberIcon,
  },
  {
    key: 'auto',
    label: 'Auto Numbers',
    short: 'Auto',
    Icon: AutoAwesomeIcon,
  },
];

/**
 * Raffle module — "NuVe System Raffle 1 — Build Specification".
 *
 * One screen: the draw flow (per raffle type), plus History, Settings
 * (manager only — the gear is not rendered for anyone else, and the server
 * refuses their writes regardless) and, when the location runs an auto-number
 * raffle, the customer's numbers. Everything is scoped to the selected
 * location (spec §2).
 */
function Raffle({ user, viewOnly }) {
  const locationId = user?.selectedLocationId;
  const [boot, setBoot] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [view, setView] = useState('draw');
  const [mode, setMode] = useState('machine');
  const [lockedMode, setLockedMode] = useState(null);
  // Settings owns the draft, so it hands its own save action up rather than
  // the toolbar keeping a second copy that could save a stale draft. The
  // handler is a ref, not state: it is replaced on every keystroke in the
  // settings form, and storing it in state would re-render this screen (and
  // rebuild the toolbar) on each one. Only `saving` needs to be reactive,
  // and useState bails out when the value has not actually changed.
  const settingsSave = useRef(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    status: '',
    message: '',
  });
  const firstLoad = useRef(true);

  // Tint the page while this screen is mounted. .rf-root only wraps its own
  // content, so on a short screen the raffle tint stopped where the content
  // did and the app's white showed below it; the scrolling surface behind
  // the page is what actually reaches the footer. Removed on the way out, so
  // no other screen inherits it.
  useEffect(() => {
    document.body.classList.add('rf-tinted');
    return () => document.body.classList.remove('rf-tinted');
  }, []);

  const notify = useCallback((status, message) => {
    if (message)
      setFeedback({
        open: true,
        status: status === 'info' ? 'warning' : status,
        message,
      });
  }, []);

  const reloadBoot = useCallback(async () => {
    if (!locationId) return null;
    const res = await raffleApi.bootstrap(locationId);
    if (!res.success) {
      setLoadError(res.message || 'The raffle screen could not load.');
      return null;
    }
    setLoadError('');
    setBoot(res);
    return res;
  }, [locationId]);

  useEffect(() => {
    firstLoad.current = true;
    setBoot(null);
    setView('draw');
    reloadBoot().then((res) => {
      // A raffle left open (reload, dropped connection) opens on its own tab.
      if (res?.openRun?.raffleNo && firstLoad.current)
        setMode(res.openRun.mode);
      firstLoad.current = false;
    });
  }, [reloadBoot]);

  const switchMode = (m) => {
    if (m === mode) return;
    if (lockedMode && lockedMode !== m) {
      notify('warning', 'Finish or close the raffle in progress first.');
      return;
    }
    setMode(m);
    setView('draw');
  };

  const onModeLock = useCallback((m) => setLockedMode(m), []);
  const registerSave = useCallback((handle) => {
    settingsSave.current = handle ? handle.save : null;
    setSettingsSaving(Boolean(handle && handle.saving));
  }, []);

  const canManage = boot?.canManage && !viewOnly;
  const hasAuto = (boot?.drawings || []).some((d) => d.auto);

  if (!locationId) {
    return (
      <div className="rf-root rf-pad">
        <Notice>Select a location to run raffles.</Notice>
      </div>
    );
  }

  return (
    <>
      {/* Not the shared FeedbackBar: it splits messages on "$" (and then
          adds "See logs"), and raffle messages are full of dollar amounts. */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          variant="filled"
          severity={feedback.status || 'info'}
          onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        >
          {feedback.message}
        </Alert>
      </Snackbar>

      {/* The app's contextual toolbar, as on Transaction: actions on the
          left, the title on the right. History / Settings are sub-screens
          with a Back action. */}
      <CustomToolbar
        title={TITLES[view] || 'Raffle'}
        createTitle=""
        backTitle=""
        headerButtons={
          view === 'draw'
            ? [
                // Only where it is useful: a location with no auto drawing
                // has no numbers to look up.
                ...(hasAuto
                  ? [
                      {
                        icon: <LocalActivityIcon />,
                        title: 'Customer raffle numbers',
                        handler: () => setView('numbers'),
                      },
                    ]
                  : []),
                // History is one clock session, so it only appears for
                // someone who has one. Manager / owner / tech admin carry
                // ROLE_ADMIN, which bypasses clock-in, so the app never
                // opens a session for them and this would only ever be an
                // empty list — they have Winners Control and the Employee
                // Session reports for the location-wide picture. Keyed on
                // the shift rather than the role, so a manager who does
                // clock in and run raffles still gets their own back.
                ...(boot?.hasShift
                  ? [
                      {
                        icon: <VisibilityIcon />,
                        title: 'History',
                        handler: () => setView('history'),
                      },
                    ]
                  : []),
                ...(canManage
                  ? [
                      {
                        icon: <SettingsIcon />,
                        title: 'Raffle Settings',
                        handler: () => setView('settings'),
                      },
                    ]
                  : []),
              ]
            : [
                {
                  icon: <ArrowBackIcon />,
                  title: 'Back',
                  handler: () => setView('draw'),
                },
                // Settings is long enough on a phone that the button at the
                // foot of it is a scroll away; this is the same action.
                ...(view === 'settings'
                  ? [
                      {
                        icon: <SaveIcon />,
                        title: 'Save settings',
                        disabled: settingsSaving,
                        handler: () =>
                          settingsSave.current && settingsSave.current(),
                      },
                    ]
                  : []),
              ]
        }
      />

      {/* Outside .rf-root on purpose: the raffle stylesheet resets every
          button inside it, which would strip the toolbar's icon buttons. */}
      <div className="rf-root">
        <div className="rf-shell">
          {view === 'draw' && (
            <div className="rf-tabs">
              {MODES.map(({ key, label, short, Icon }) => {
                const on = mode === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className="rf-tab"
                    data-on={on ? '1' : '0'}
                    onClick={() => switchMode(key)}
                  >
                    <Icon
                      style={{ fontSize: 17, color: on ? INDIGO : SLATE }}
                    />
                    <span className="rf-tab-long">{label}</span>
                    <span className="rf-tab-short">{short}</span>
                  </button>
                );
              })}
            </div>
          )}

          {!boot && !loadError && (
            <div className="rf-pad rf-center" style={{ minHeight: 200 }}>
              <Spinner color={SLATE} size={28} />
            </div>
          )}
          {loadError && (
            <div className="rf-pad">
              <Notice tone="red">{loadError}</Notice>
              <button
                type="button"
                className="rf-btn rf-btn-outline rf-w100"
                style={{ marginTop: 12 }}
                onClick={reloadBoot}
              >
                Try again
              </button>
            </div>
          )}

          {/* Kept mounted while History / Settings are open so a half-finished
            payment (prize, customer, photo) survives a look at History. */}
          {boot && (
            <div style={{ display: view === 'draw' ? 'block' : 'none' }}>
              <DrawFlow
                key={`${locationId}:${mode}`}
                mode={mode}
                boot={boot}
                locationId={locationId}
                viewOnly={viewOnly}
                notify={notify}
                reloadBoot={reloadBoot}
                onModeLock={onModeLock}
              />
            </div>
          )}
          {boot && view === 'numbers' && (
            <NumbersView boot={boot} locationId={locationId} notify={notify} />
          )}
          {boot && view === 'history' && (
            <HistoryView
              boot={boot}
              locationId={locationId}
              locationName={boot.locationName || user?.selectedLocationName}
              viewOnly={viewOnly}
              notify={notify}
            />
          )}
          {boot && view === 'settings' && canManage && (
            <SettingsView
              boot={boot}
              locationId={locationId}
              locationName={boot.locationName || user?.selectedLocationName}
              notify={notify}
              reloadBoot={reloadBoot}
              registerSave={registerSave}
              onDone={() => setView('draw')}
            />
          )}
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: userInfoSelector(state),
  viewOnly: viewOnlySelector(state),
});

function RaffleScreen(props) {
  return (
    <RaffleErrorBoundary>
      <Raffle {...props} />
    </RaffleErrorBoundary>
  );
}

export default withShipment({ mapStateToProps }, RaffleScreen);
